import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { motion } from 'framer-motion';
import { liveQuery } from 'dexie';

import type { Moment } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import { listMoments } from './momentRepo';
import { subscribeMomentAccount } from './accountScope';
import { addMomentComment, deleteMomentComment, loadMomentInteraction, toggleMomentLike } from './interactionRepo';
import type { MomentInteraction } from './interactionRepo';
import styles from './moments.module.css';

function displayTime(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function imageSource(avatar: string | undefined): string | null {
  return avatar && /^(data:image\/|https?:\/\/|blob:)/i.test(avatar) ? avatar : null;
}

function firstText(...values: (string | undefined)[]): string {
  return values.find((value) => value !== undefined && value.trim() !== '')?.trim() ?? '';
}

function MomentCard({ moment }: { moment: Moment }): ReactElement {
  const presets = useMotionPresets();
  const avatar = imageSource(moment.authorAvatar);
  const [interaction, setInteraction] = useState<MomentInteraction | null>(null);
  const [commenting, setCommenting] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const observe = () => liveQuery(() => loadMomentInteraction(moment.id)).subscribe({
      next: setInteraction,
      error: () => setError('读取互动失败，请重试'),
    });
    let subscription = observe();
    const unsubscribe = subscribeMomentAccount(() => {
      setInteraction(null); setComment(''); setCommenting(false); setError(null);
      subscription.unsubscribe(); subscription = observe();
    });
    return () => { subscription.unsubscribe(); unsubscribe(); };
  }, [moment.id]);
  async function act(action: () => Promise<void>, after?: () => void): Promise<void> {
    if (busy) return;
    setBusy(true); setError(null);
    try { await action(); after?.(); }
    catch (failure) { setError(failure instanceof Error ? failure.message : '保存失败，请重试'); }
    finally { setBusy(false); }
  }
  return (
    <motion.article
      className={styles.card}
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={presets.tap.transition}
      data-skin-surface="panel"
    >
      <div className={styles.avatar} aria-hidden="true">
        {avatar ? <img src={avatar} alt="" /> : firstText(moment.authorAvatar, moment.authorName.charAt(0), '友')}
      </div>
      <div className={styles.body}>
        <strong className={styles.author}>{moment.authorName}</strong>
        {moment.content ? <p className={styles.content}>{moment.content}</p> : null}
        {moment.images.length > 0 ? (
          <div className={styles.images} data-count={Math.min(3, moment.images.length)}>
            {moment.images.map((image, index) => <img key={`${moment.id}-${String(index)}`} src={image} alt={`朋友圈图片 ${String(index + 1)}`} />)}
          </div>
        ) : null}
        <time className={styles.time} dateTime={new Date(moment.createdAt).toISOString()}>{displayTime(moment.createdAt)}</time>
        <div className={styles.interactionActions}>
          <button type="button" aria-label={`点赞 ${moment.authorName}的朋友圈`} aria-pressed={interaction?.liked ?? false} disabled={busy}
            onClick={() => { void act(() => toggleMomentLike(moment.id)); }}>
            {interaction?.liked ? '♥ 已点赞' : '♡ 点赞'}
          </button>
          <button type="button" aria-label={`评论 ${moment.authorName}的朋友圈`} aria-expanded={commenting}
            onClick={() => setCommenting(value => !value)}>评论{interaction?.comments.length ? ` (${String(interaction.comments.length)})` : ''}</button>
        </div>
        {interaction?.comments.length ? <ul className={styles.comments} aria-label="我的评论">
          {interaction.comments.map(item => <li key={item.id}>
            <span><strong>我：</strong>{item.content}</span>
            <button type="button" disabled={busy} aria-label={`删除评论：${item.content}`}
              onClick={() => { void act(() => deleteMomentComment(moment.id, item.id)); }}>删除</button>
          </li>)}
        </ul> : null}
        {commenting ? <form className={styles.commentForm} onSubmit={event => {
          event.preventDefault(); void act(() => addMomentComment(moment.id, comment), () => { setComment(''); setCommenting(false); });
        }}>
          <label className={styles.commentLabel}>评论内容
            <textarea value={comment} maxLength={500} rows={2} placeholder="写下你的评论…" disabled={busy}
              onChange={event => setComment(event.currentTarget.value)} />
          </label>
          <button type="submit" disabled={busy || !comment.trim()}>{busy ? '保存中…' : '发送评论'}</button>
        </form> : null}
        {error ? <p className={styles.interactionError} role="alert">{error}</p> : null}
      </div>
    </motion.article>
  );
}

export function MomentsPage(): ReactElement {
  const [moments, setMoments] = useState<Moment[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useComponentMeta<HTMLElement>(buildComponentMeta({
    id: 'page.moments.root', page: 'moments', role: 'container', label: '朋友圈页面容器',
    layout: { x: 0, y: 24, width: 390, height: 744 }, zIndex: 2,
  }, 'container'));
  const headerRef = useComponentMeta<HTMLDivElement>(buildComponentMeta({
    id: 'moments.header', page: 'moments', role: 'container', label: '朋友圈标题栏',
    layout: { x: 0, y: 24, width: 390, height: 58 }, zIndex: 3,
  }, 'container'));
  const listRef = useComponentMeta<HTMLDivElement>(buildComponentMeta({
    id: 'moments.list', page: 'moments', role: 'list', label: '朋友圈动态列表',
    layout: { x: 12, y: 92, width: 366, height: 650 }, zIndex: 3,
  }, 'list'));

  useEffect(() => {
    const observe = () => liveQuery(() => listMoments()).subscribe({
      next: (rows) => {
        setMoments(rows);
        setLoaded(true);
      },
      error: () => setError('读取朋友圈失败'),
    });
    let subscription = observe();
    const unsubscribeAccount = subscribeMomentAccount(() => {
      setMoments([]);
      subscription.unsubscribe();
      subscription = observe();
    });
    return () => { subscription.unsubscribe(); unsubscribeAccount(); };
  }, []);

  return (
    <section ref={rootRef} className={styles.page} data-ui-id="page.moments.root" data-ui-role="container" data-skin-surface="page">
      <div ref={headerRef} className={styles.header} data-ui-id="moments.header" data-ui-role="container">
        <h1>朋友圈</h1>
      </div>
      <div ref={listRef} className={styles.list} data-ui-id="moments.list" data-ui-role="list">
        {moments.map((moment) => <MomentCard key={moment.id} moment={moment} />)}
        {loaded && moments.length === 0 ? (
          <div className={styles.empty}>
            <h2>朋友圈还是空的</h2>
            <p>开启角色的「自动朋友圈」后，动态会出现在这里。</p>
          </div>
        ) : null}
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
      </div>
    </section>
  );
}
